import { TableName } from '../../../../../tables'
import { UIPermission, DetailPanel, DetailFieldProps, FieldType, FieldValue, Action, ModalId, StaticValue, useUIActions, Tab, useGetCrumbs, Link } from '../../../../../features/ui'
import { Customer, HoardOrder, HoardType, SalePaymentItem } from '../../../state/types'
import { useEffect, useMemo, useState } from 'react'
import { OperationItemsTable, CloseOperationModal, PriceBreakdown, PaymentStatus, usePriceOutlineHooks, operationHelpers } from '../../../../operations'
import { CompanyUserName, useSystemStateHooks } from '../../../../system'
import { Check, CreditPayment, InvoiceRequestedModal, PaymentCategory, useEmployeeHooks, useEmployeeLoaders, usePaymentTypeLoaders, InvoiceStatusField, AdjustmentNote } from '../../../../administration'
import { RoutePath } from '../../../../../features/routes'
import { DirectArchiveModal, Labels, useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { HoardValues } from '../hoard-values/hoardValues'
import { FinancialHoardItems } from '../financial-hoard-items/financialHoardItems'
import { useLocation, useNavigate } from 'react-router-dom'
import { BranchBadge } from '../../../../manager'
import { hoardHelpers } from '../../../hooks/hoardHooks'
import { SalePaymentItemsTable } from '../../sale-operation/sale-payment-items-table/salePaymentItemsTable'
import { UploadSaleReceiptFormModal } from '../../sale-operation/upload-sale-receipt-modal/uploadSaleReceiptFormModal'
import { HoardPricesList } from '../hoard-prices-list/hoardPricesList'
import { CustomerLink } from '../../customer/customer-link/customerLink'
import { saleOperationHelpers, useSaleOperationHooks } from '../../../hooks/saleOperationHooks'
import { EditHoardDueDateModal } from '../hoard-due-date/editHoardDueDateModal'
import { OperationCreditPaymentsTable } from '../../sale-operation/operation-credit-payments-table/operationCreditPaymentsTable'
import { FiscalDocument, FiscalDocumentStatus, getActiveInvoices, getNetInvoicedHoardValue, getNetInvoicedTotal } from '../../../../fiscal-documents'
import { Delivery, DeliveryOrder, deliveryOrderHelpers, DeliveryOrdersDataList, DeliveryStatus, DownloadDeliveryNoteModal, UploadDeliveryNoteModal, useDeliveryHooks } from '../../../../deliveries'
import { DownloadSaleFilesModal } from '../../sale-operation/download-sale-files-modal/downloadSaleFilesModal'
import { SaleOperationDocumentsPanel } from '../../sale-operation/documents-panel/saleOperationDocumentsPanel'
import { DownloadReceiptModal } from '../../../../common'
import { NotesTabContent } from '../../sale-operation/notes/notesTabContent'
import { AddNoteModal } from '../../sale-operation/notes/addNoteModal'
import { useHoardActions } from '../../../actions/hoardActions'
import { useAddNote } from '../../../hooks/notesHooks'
import { CancelHoardModal } from '../cancel-hoard-modal/cancelHoardModal'
import { CloseFinancialHoardModal } from '../close-financial-hoard-modal/closeFinancialHoardModal'
import { useHoardHooks } from '../../../hooks/hoardHooks'
import { CorrectHoardPriceModal } from '../correct-hoard-price-modal/correctHoardPriceModal'

type Props = {
  hoard: HoardOrder
  deliveryOrders: DeliveryOrder[]
  deliveries: Delivery[]
  checks: Check[]
  customer?: Customer
  creditPayments: CreditPayment[]
  fiscalDocuments: FiscalDocument[]
  adjustmentNotes: AdjustmentNote[]
  onRefreshDeliveries?: () => void
  onRefreshFiscalDocuments?: () => Promise<void>
  isAutoRefreshExhausted?: boolean
}

const sortByIssueDate = (invoices: FiscalDocument[]) =>
  [...invoices].sort((invoiceA, invoiceB) => invoiceA.issueDate.localeCompare(invoiceB.issueDate))

export const HoardDetail = (props: Props) => {
  const { hoard, deliveryOrders, checks, customer, creditPayments, fiscalDocuments, adjustmentNotes, onRefreshDeliveries, onRefreshFiscalDocuments, isAutoRefreshExhausted = false } = props
  const { items, paymentItems, pricesList, priceCorrections, events, perceptions, deliveryData, internalNotes, invoiceStats, invoiceInFlight, specialDiscountItems: _specialDiscountItems, ...data } = hoard

  const [deliveries, setDeliveries] = useState<Delivery[]>(props.deliveries)

  useEffect(() => {
    setDeliveries(props.deliveries)
  }, [props.deliveries])
  const [currentPaymentItem, setCurrentPaymentItem] = useState<SalePaymentItem>()
  const [currentDelivery, setCurrentDelivery] = useState<Delivery>()
  
  const { isInvoiceable, isClosable, isFinancialHoardCancellable, hasInvoicedPendingItems } = hoardHelpers
  const { isArchivable, sortPaymentItems } = saleOperationHelpers
  const { getPendingCredit } = useHoardHooks()
  const { describeCode } = operationHelpers
  const { isEditableSync, isDeliveryOrderEmitable, isPayable, isManualCreditNoteAllowed } = useSaleOperationHooks()
  const { describe } = useDescribeHooks()
  const { formatDateTime } = useDataTypesHooks()
  const { describeEmployee } = useEmployeeHooks()
  const { userHasPermission } = useSystemStateHooks()
  const { refreshDeliveries } = useDeliveryHooks()
  const { getPriceOutline } = usePriceOutlineHooks()
  const { formatDate } = useDataTypesHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_HOARDS) || hoard.cancelled || hoard.archived
  const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
  const hasInvoicePermission = userHasPermission(UIPermission.INVOICE_HOARDS)
  const canAnnulInvoice = hasInvoicePermission && !hoard.cancelled && !hoard.archived
  const deliveriesReadOnly = !hasDeliveryPermission || !!hoard.cancelled || !!hoard.archived
  const navigate = useNavigate()
  const locationFrom = useLocation().state?.from
  
  const employeeLoaders = useEmployeeLoaders()
  const paymentTypeLoaders = usePaymentTypeLoaders()
  
  const { openModal } = useUIActions()
  const { saveHoard } = useHoardActions()
  
  const title = useMemo(() => describe(TableName.HOARD_ORDERS, hoard), [hoard])
  const fromCreditAccounts = locationFrom === RoutePath.CREDIT_ACCOUNTS
  const crumbs = useGetCrumbs(
    fromCreditAccounts ? 'Cuentas Corrientes' : 'Acopios',
    locationFrom || RoutePath.HOARDS,
    fromCreditAccounts ? title : describeCode(hoard)
  )
  
  const findDeliveryOrder = (delivery?: Delivery) => deliveryOrders.find(order => order.id === delivery?.orderId)
  
  const isProductHoard = useMemo(() => hoard.type === HoardType.PRODUCT, [hoard.type])
  const paymentType = useMemo(() => paymentTypeLoaders().findPaymentType(hoard.paymentTypeId), [hoard.paymentTypeId])
  const sortedPaymentItems = useMemo(() => sortPaymentItems(hoard), [hoard])
  const isCreditPayment = useMemo(() => paymentType?.category === PaymentCategory.CREDIT_ACCOUNT, [paymentType])
  const isReturnable = useMemo(() => deliveryOrders.some(deliveryOrder => deliveryOrderHelpers.isReturnable(deliveryOrder)), [deliveryOrders])
  const isCancellable = useMemo(() => deliveryOrderHelpers.isCancellable(hoard, deliveryOrders), [hoard, deliveryOrders])
  const isHoardCancellable = useMemo(() => isFinancialHoardCancellable(hoard, deliveryOrders), [hoard, deliveryOrders])
  const currentDeliveryOrder = useMemo(() => findDeliveryOrder(currentDelivery), [currentDelivery])

  // Un acopio puede tener varias facturas parciales: el estado del badge es el agregado de la
  // operación y cada factura se muestra/acciona por separado en Documentos. El borrador (PREVIEW)
  // no es un comprobante y la factura anulada no es la factura activa: ninguna de las dos cuenta
  // como factura emitida para el estado ni para volver a facturar.
  const invoices = useMemo(
    () => getActiveInvoices(fiscalDocuments),
    [fiscalDocuments]
  )
  const pendingInvoice = useMemo(
    () => invoices.find(invoice => invoice.status === FiscalDocumentStatus.TO_AUTHORIZE),
    [invoices]
  )
  const rejectedInvoice = useMemo(() => {
    const rejectedInvoices = sortByIssueDate(invoices.filter(invoice => invoice.status === FiscalDocumentStatus.REJECTED))
    return rejectedInvoices[rejectedInvoices.length - 1]
  }, [invoices])
  const latestInvoice = useMemo(() => {
    const sortedInvoices = sortByIssueDate(invoices)
    return sortedInvoices[sortedInvoices.length - 1]
  }, [invoices])
  // En producto el total facturado es el de los comprobantes; en financiero se compara en espacio de
  // valor (con IVA, sin recargo), igual que "Crédito Utilizado" y "Restante".
  const invoicedTotal = useMemo(
    () => isProductHoard
      ? getNetInvoicedTotal(fiscalDocuments)
      : getNetInvoicedHoardValue(fiscalDocuments, hoard.paymentTypeYield),
    [isProductHoard, fiscalDocuments, hoard.paymentTypeYield]
  )

  const onDownloadReceipt = (index: string) => {
    setCurrentPaymentItem(sortedPaymentItems.find((_, i) => i.toString() === index)!)
    openModal(ModalId.DOWNLOAD_RECEIPT)
  }

  const onUploadReceipt = (index: string) => {
    setCurrentPaymentItem(sortedPaymentItems.find((_, i) => i.toString() === index)!)
    openModal(ModalId.UPLOAD_SALE_RECEIPT)
  }

  const fields: DetailFieldProps[] = [
    {
      name: 'sellerId',
      type: FieldType.TABLE,
      table: TableName.EMPLOYEES,
      label: 'Vendedor',
      render: (value?: FieldValue) => {
        const seller = employeeLoaders().findEmployee(value! as string)!
        return <>{describeEmployee(seller)}</>
      }
    },
    {
      name: 'customerId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMERS,
      label: 'Cliente',
      loading: !customer,
      render: () => <CustomerLink customer={customer!} />
    },
    {
      name: 'customerTypeId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMER_TYPES,
      label: 'Lista de Precios'
    },
    {
      name: 'branchId',
      type: FieldType.TABLE,
      table: TableName.BRANCHES,
      label: 'Sucursal de Entrega',
      render: () => <BranchBadge branchId={hoard.branchId} />
    },
    {
      name: 'dueDate',
      type: FieldType.DATE,
      label: 'Fecha Límite',
      render: (value?: FieldValue) => {
        if (!value) return <>-</>
        const dateObject = new Date(value as string)
        // Sin permiso, cancelado o archivado: la fecha límite no se puede modificar
        if (readOnly) return <>{formatDate(dateObject)}</>
        return (
          <Link
            text={formatDate (dateObject)}
            kind="primary"
            onClick={() => openModal(ModalId.EDIT_HOARD_DUE_DATE)}
          />
        )
      },
    },
    {
      name: 'priceBreakdown',
      type: FieldType.PRICE,
      label: 'Precio',
      render: () => {
        const priceOutline = getPriceOutline(TableName.HOARD_ORDERS, hoard)
        return <PriceBreakdown data={priceOutline} />
      }
    },
    {
      name: 'paymentStatus',
      type: FieldType.TEXT,
      label: 'Estado de Pago',
      render: (value?: FieldValue) => <>{Labels.paymentStatusLabel(value! as PaymentStatus)}</>
    },
    {
      name: 'invoiceStatus',
      type: FieldType.TEXT,
      label: 'Facturación',
      render: () => <InvoiceStatusField
        invoices={invoices}
        status={hoard.invoiceStatus}
        onRefresh={async () => { await onRefreshFiscalDocuments?.() }}
        onRegenerate={(regeneratedInvoice) => navigate(`${RoutePath.INVOICE}?fiscalDocumentId=${regeneratedInvoice.id}`)}
        isAutoRefreshExhausted={isAutoRefreshExhausted}
      />
    },
    {
      name: 'deliveryStatus',
      type: FieldType.TEXT,
      label: 'Estado de Entrega',
      render: (value?: FieldValue) => <>{Labels.deliveryStatusLabel(value! as DeliveryStatus)}</>
    }
  ]

  if (hoard.paymentStatus === PaymentStatus.PARTIALLY_PAYED) {
    const paidAmountField = {
      name: 'paidAmount',
      type: FieldType.PRICE,
      label: 'Monto Pagado'
    }
    fields.splice(6, 0, paidAmountField)
  }
  // En cuenta corriente también puede haber pagos directos (ampliaciones cobradas en el momento)
  if (sortedPaymentItems.length > 0) {
    const paymentItemsField = {
      name: 'paymentItems',
      type: FieldType.TABLE,
      render: () => (
        <SalePaymentItemsTable
          salePaymentItems={sortedPaymentItems.map((paymentItem, index) => ({
            index: index.toString(),
            paymentTypeId: paymentItem.paymentTypeId,
            creditInFavor: paymentItem.creditInFavor,
            isRounding: paymentItem.isRounding,
            cashFundId: paymentItem.destinationId,
            amount: paymentItem.amount,
            checkName: checks.find(check => check.id === paymentItem.destinationId)?.name,
            receiptUrl: paymentItem.receiptUrl
          }))}
          createMode={false}
          readOnly={readOnly}
          onDownloadReceipt={onDownloadReceipt}
          onUploadReceipt={onUploadReceipt}
        />
      )
    }
    fields.splice(5, 0, paymentItemsField)
  }
  if (isCreditPayment && creditPayments.length > 0) {
    const creditPaymentsField = {
      name: 'creditPayments',
      type: FieldType.TABLE,
      render: () => (
        <OperationCreditPaymentsTable
          operationId={hoard.id}
          creditPayments={creditPayments}
        />
      )
    }
    fields.splice(5, 0, creditPaymentsField)
  }
  if (isCreditPayment || sortedPaymentItems.length === 0) {
    const paymentTypeField = {
      name: 'paymentTypeId',
      type: FieldType.TABLE,
      table: TableName.PAYMENT_TYPES,
      label: 'Medio de Pago'
    }
    fields.splice(5, 0, paymentTypeField)
  }
  if (items.length > 0) {
    const isFinancial = hoard.type === HoardType.FINANCIAL
    const itemsField = {
      name: 'items',
      type: FieldType.TABLE,
      render: () => isFinancial ? (
        <FinancialHoardItems
          hoard={hoard}
          deliveryOrders={deliveryOrders}
        />
      ) : (
        <OperationItemsTable
          itemsData={items}
          createMode={false}
          readOnly
        />
      )
    }
    fields.splice(3, 0, itemsField)
  }
  // Inserted after the other fields so it always stays right after the prices list
  if (!isProductHoard) {
    const hoardValueField = {
      name: 'value',
      type: FieldType.PRICE,
      label: 'Valor Acopiado',
      render: () => (
        <HoardValues hoard={hoard} />
      )
    }
    const customerTypeIndex = fields.findIndex(field => field.name === 'customerTypeId')
    fields.splice(customerTypeIndex + 1, 0, hoardValueField)
  }
  if (invoices.length > 0) {
    const invoicedTotalField = {
      name: 'invoicedTotal',
      type: FieldType.PRICE,
      label: 'Total Facturado',
      render: () => <StaticValue type={FieldType.PRICE} value={invoicedTotal} />
    }
    const invoiceStatusIndex = fields.findIndex(field => field.name === 'invoiceStatus')
    fields.splice(invoiceStatusIndex + 1, 0, invoicedTotalField)
  }
  if (hoard.notes) {
    const notesField = {
      name: 'notes',
      type: FieldType.TEXT,
      label: 'Notas'
    }
    fields.push(notesField)
  }
  if (hoard.negativeStateUserId) {
    const negativeStateLabel = hoard.cancelled ? 'Cancelado por' : 'Archivado por'
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: negativeStateLabel,
      render: () => {
        const dateStr = hoard.negativeStateAt ? ` - ${formatDateTime(new Date(hoard.negativeStateAt))}` : ''
        return <><CompanyUserName userId={hoard.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const onDownloadFiles = () => openModal(ModalId.DOWNLOAD_FILES)

  const onEdit = () => navigate(RoutePath.EDIT)

  const onIncreaseHoard = () => navigate(RoutePath.INCREASE_HOARD)

  const onEmitDelivery = () => navigate(RoutePath.EMIT_DELIVERY)

  const onConfirmPayment = () => navigate(RoutePath.CONFIRM_PAYMENT)

  // Un acopio financiero con crédito pendiente pregunta si el restante se acredita al cliente
  const onClose = () => openModal(!isProductHoard && getPendingCredit(hoard) > 0 ? ModalId.CLOSE_FINANCIAL_HOARD : ModalId.CLOSE_OPERATION)

  const onInvoice = () => navigate(RoutePath.INVOICE)

  const onReturnItems = () => navigate(RoutePath.REGISTER_RETURN)

  const onCancelItems = () => navigate(RoutePath.CANCEL_ITEMS)

  const onArchive = () => openModal(ModalId.DIRECT_ARCHIVE)

  const onCancelHoard = () => openModal(ModalId.CANCEL_HOARD)

  const onManualCreditNote = () => navigate(RoutePath.MANUAL_CREDIT_NOTE)

  // A financial hoard keeps the prices list it was created with: a wrong price can be corrected.
  // With an invoice in flight the price can't change (the invoice may be allocating those units).
  const canCorrectPrices = !readOnly && hoard.type === HoardType.FINANCIAL && !invoiceInFlight
  const onCorrectPrice = () => openModal(ModalId.CORRECT_HOARD_PRICE)

  const handleAddNote = useAddNote(hoard, async (updated: HoardOrder) => {
    await saveHoard(updated)
  })

  const actions: Action[] = useMemo(() => {
    const actionList: Action[] = [
      { label: 'Documentos', kind: 'tertiary', icon: 'download', onClick: onDownloadFiles }
    ]
    const isEditable = isEditableSync(hoard)
    if (!readOnly && isManualCreditNoteAllowed(TableName.HOARD_ORDERS, hoard, isEditable)) {
      actionList.push({ label: 'Nota de Crédito', icon: 'file-earmark-minus', kind: 'quinary', onClick: onManualCreditNote })
    }

    if (!readOnly) {
      if (isEditable) {
        actionList.push({ label: 'Editar Acopio', icon: 'pencil-fill', onClick: onEdit })
      }
      if (isPayable(hoard)) {
        actionList.push({ label: 'Confirmar Pago', icon: 'check-lg', onClick: onConfirmPayment })
      }
    }
    const isPayed = hoard.paymentStatus === PaymentStatus.PAYED
    // Solo se amplía un acopio pagado (o en cuenta corriente), así no se acumulan ampliaciones sin cobrar
    const isIncreasable = !isProductHoard && !readOnly && (isPayed || isCreditPayment) && !hoard.cancelled && !hoard.archived
    if (isIncreasable) {
      actionList.push({ label: 'Ampliar Acopio', icon: 'cash-coin', onClick: onIncreaseHoard })
    }
    if (hasDeliveryPermission) {
      if (isDeliveryOrderEmitable(hoard)) {
        actionList.push({ label: 'Emitir Entrega', icon: 'calendar-range', onClick: onEmitDelivery })
      }
      if (isReturnable) {
        actionList.push({ label: 'Registrar Devolución', icon: 'arrow-return-left', onClick: onReturnItems })
      }
      if (isClosable(hoard) && !hasInvoicedPendingItems(hoard, deliveryOrders)) {
        actionList.push({ label: 'Cerrar Acopio', icon: 'bag-check-fill', onClick: onClose })
      }
    }
    if (hasInvoicePermission && isInvoiceable(hoard) && !pendingInvoice) {
      actionList.push({ label: 'Facturar', icon: 'file-earmark-check', kind: 'quinary', onClick: onInvoice })
    }
    if (!deliveriesReadOnly && isCancellable) {
      actionList.push({ label: 'Cancelar Productos', icon: 'x-circle', kind: 'secondary', onClick: onCancelItems })
    }
    if (!readOnly && isHoardCancellable) {
      actionList.push({ label: 'Cancelar Acopio', icon: 'slash-circle', kind: 'secondary', onClick: onCancelHoard })
    }
    if (!readOnly && isArchivable(hoard)) {
      actionList.push({ label: 'Archivar Acopio', icon: 'archive-fill', kind: 'secondary', onClick: onArchive })
    }

    return actionList
  }, [hoard, hasDeliveryPermission, hasInvoicePermission, isEditableSync, isPayable, isDeliveryOrderEmitable, isReturnable, isCancellable, isHoardCancellable, isArchivable, isClosable, deliveryOrders, pendingInvoice])

  const tabs = useMemo(() => {
    const tabList: Tab[] = [
      {
        id: 'documents',
        title: 'Documentos',
        content: (
          <SaleOperationDocumentsPanel
            table={TableName.HOARD_ORDERS}
            operation={hoard}
            deliveries={deliveries}
            deliveryOrders={deliveryOrders}
            creditPayments={creditPayments}
            fiscalDocuments={fiscalDocuments}
            adjustmentNotes={adjustmentNotes}
            deliveriesReadOnly={deliveriesReadOnly}
            canAnnulInvoice={canAnnulInvoice}
            onDownloadDeliveryNote={setCurrentDelivery}
            onRefreshDeliveries={onRefreshDeliveries}
            onRefreshFiscalDocuments={onRefreshFiscalDocuments}
            isAutoRefreshExhausted={isAutoRefreshExhausted}
          />
        )
      },
      {
        id: 'pricesList',
        title: 'Lista de Precios',
        content: <HoardPricesList
          pricesList={pricesList}
          priceCorrections={priceCorrections}
          onCorrectPrice={canCorrectPrices ? onCorrectPrice : undefined}
        />
      }
    ]

    tabList.push({
      id: 'deliveryOrders',
      title: 'Entregas',
      content: <DeliveryOrdersDataList
        deliveryData={deliveryData}
        deliveryOrders={deliveryOrders}
        deliveries={deliveries}
        operation={hoard}
        customer={customer}
        branchId={hoard.branchId}
        readOnly={deliveriesReadOnly}
        hideDeliveries
        onChangeCurrentDelivery={setCurrentDelivery}
        onRefresh={onRefreshDeliveries}
      />
    })
    tabList.push({
      id: 'notes',
      title: 'Notas',
      content: <NotesTabContent 
        originalNote={hoard.notes}
        originalDate={hoard.creationDate} 
        internalNotes={hoard.internalNotes || []} 
      />
    })
    
    return tabList
  }, [hoard, customer, deliveries, deliveryOrders, deliveryData, creditPayments, fiscalDocuments, adjustmentNotes, hoard.internalNotes, hoard.notes, hasDeliveryPermission, canAnnulInvoice, onRefreshDeliveries, isAutoRefreshExhausted])

  const onUploadDeliveryNote = async () => {
    const refreshedDeliveries = await refreshDeliveries(deliveries)
    setDeliveries(refreshedDeliveries)
  }

  return (<>
    <DetailPanel
      title={title}
      tableName={TableName.HOARD_ORDERS}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='layers'
      imgSpace={3}
      tabs={tabs}
      readOnly={readOnly}
      actions={actions}
    />
    <DownloadSaleFilesModal
      table={TableName.HOARD_ORDERS}
      saleOperation={hoard}
    />
    <DownloadReceiptModal operation={currentPaymentItem} />
    <DownloadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={findDeliveryOrder(currentDelivery)}
    />
    <UploadSaleReceiptFormModal
      table={TableName.HOARD_ORDERS}
      operation={hoard}
      paymentItem={currentPaymentItem}
    />
    <UploadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={currentDeliveryOrder}
      onUpload={onUploadDeliveryNote}
    />
    <CloseOperationModal
      table={TableName.HOARD_ORDERS}
      operation={hoard}
    />
    {latestInvoice && (
      <InvoiceRequestedModal
        operationTable={TableName.HOARD_ORDERS}
        fiscalDocument={latestInvoice}
      />
    )}
    <EditHoardDueDateModal hoard={hoard} />
    <DirectArchiveModal
      tableName={TableName.HOARD_ORDERS}
      dataType={hoard}
    />
    <CancelHoardModal hoard={hoard} />
    {!isProductHoard && (
      <CloseFinancialHoardModal
        hoard={hoard}
        customer={customer}
        onClosed={onRefreshFiscalDocuments}
      />
    )}
    <AddNoteModal onSubmit={handleAddNote} />
    {canCorrectPrices && (
      <CorrectHoardPriceModal
        hoard={hoard}
        deliveryOrders={deliveryOrders}
        onCorrected={onRefreshFiscalDocuments}
      />
    )}
  </>)
}
